// Shared formatting + the category/feature color language for the AI Operations
// console. Two fixed hues track the Ops vs Tenant-facing split everywhere.

export const COST_ACCENT = '#0ea5e9'; // sky — the "spend" hue (the console's thesis)

export const OPS_META = { label: 'Ops Agents', color: '#7c3aed' }; // violet
export const TENANT_META = { label: 'Tenant-Facing', color: '#0d9488' }; // teal
// The embedded website assistant. Amber because it is the one category whose
// spend comes from anonymous strangers — worth noticing, not alarming.
export const PUBLIC_META = { label: 'Website Chat', color: '#d97706' }; // amber

export const CATEGORY_META: Record<string, { label: string; color: string }> = {
  ops: OPS_META,
  tenant_facing: TENANT_META,
  public: PUBLIC_META,
};

// Stable palette for feature/model/agent series in charts.
export const SERIES_COLORS = ['#0ea5e9', '#7c3aed', '#0d9488', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#10b981'];

export const FEATURE_LABEL: Record<string, string> = {
  messaging: 'Messaging',
  analysis: 'Analysis & Compliance',
  documents: 'Documents & Forms',
  cowork: 'CoWork',
  ops: 'Ops Agents',
  ocr: 'Document OCR',
  website_chat: 'Website Chat',
};

export function formatUsd(n: number, opts?: { precise?: boolean }): string {
  if (!Number.isFinite(n)) return '$0.00';
  if (opts?.precise && n > 0 && n < 0.01) return `$${n.toPrecision(2)}`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: n !== 0 && Math.abs(n) < 100 ? 2 : 0,
  }).format(n);
}

export function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n ?? 0);
}

export function colorForCategory(category: string): string {
  return CATEGORY_META[category]?.color ?? '#64748b';
}

// One-line "when it's used" note per agent key — shown under the description in
// the Agents & Models tab so a super-admin knows what each row actually drives.
export const AGENT_USAGE_NOTES: Record<string, string> = {
  'ai.generate_form': 'Fires when a tenant generates a new form from a prompt in the Form builder.',
  'ai.edit_form': 'Fires when a tenant edits a form with a natural-language instruction.',
  'ai.generate_ndf': 'Fires when generating a form or hybrid document from a prompt.',
  'ai.generate_ndf_document': 'Fires when a tenant generates a full legal document from a prompt (the heavy, long-output path).',
  'ai.repair_document': 'Automatic second pass that fixes defects in a freshly generated legal document.',
  'ai.edit_ndf': 'Fires when a tenant edits an existing document with an instruction.',
  'ai.classify_document': 'Classifies a generation prompt (document vs form vs message) before routing it.',
  'classification.purpose': 'Runs in the Send flow to label a message Marketing vs Informational for consent gating.',
  'compliance.check': 'Runs in the Send flow to score TCPA / CAN-SPAM compliance of the content.',
  cowork: 'Powers the tenant CoWork assistant — 1–8 model rounds per user turn as it calls tools.',
  'composer.sms': 'Writes SMS content when a tenant uses AI compose in the Send wizard.',
  'composer.email': 'Writes email content when a tenant uses AI compose in the Send wizard.',
  'composer.voice': 'Writes voice-call scripts when a tenant uses AI compose.',
  'composer.multi': 'Writes SMS + email + voice together for multi-channel sends.',
  'reply.assistant': 'Drafts a reply when a tenant clicks “Suggest reply” in the inbox.',
  'analyzer.channel': 'Analyzes channel + compliance for content; used inline by compose/reply checks.',
  'content.advisor': 'Generates tenant content suggestions (forms, templates) on the dashboard.',
  'ocr.mistral': 'Extracts text when a tenant imports or uploads a document (PDF, DOCX, image).',
  'ops.observability': 'Super-Admin AI console — queries live App Insights telemetry.',
  'ops.documentation': 'Super-Admin AI console — reads the repo and drafts docs.',
  'ops.support': 'Super-Admin AI console — triages support tickets and drafts replies.',
  'ops.marketing': 'Super-Admin AI console — drafts campaigns and on-brand copy.',
  'ops.product': 'Super-Admin AI console — answers product questions from the KB + live code.',
  'ops.product-analyst': 'Super-Admin AI console — answers analytics questions via read-only SQL.',
  'public.website-expert':
    'Powers the chat widget embedded on respectly.ai and other public sites — anonymous visitors, one or two model rounds per question. Each widget answers only from the documents chosen for it in Chat Widgets.',
};
